import { useState } from 'react';
import { Link, usePage } from '@inertiajs/react';
import { Menu, X, Stethoscope, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Logo from '../logo';

const navLinks = [
  { href: '/come-funziona', label: 'Come funziona' },
  { href: '/perche-emovia', label: 'Perché Emovia' },
  { href: '/prezzi', label: 'Prezzi' },
  { href: '/sicurezza', label: 'Sicurezza' },
  { href: '/blog', label: 'Blog' },
];

const loginLinks = [
  { href: '/login?role=patient', label: 'Accedi come Cliente', icon: User },
  { href: '/login?role=therapist', label: 'Accedi come Psicologo', icon: Stethoscope },
];

type LoginMenuProps = {
  align?: 'start' | 'center' | 'end';
  buttonClassName?: string;
  forceFullReload?: boolean;
};

function LoginMenu({ align = 'end', buttonClassName, forceFullReload = false }: LoginMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className={buttonClassName}>Login</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        {loginLinks.map(({ href, label, icon: Icon }) => (
          <DropdownMenuItem key={href} asChild>
            {forceFullReload ? (
              <a href={href} className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                {label}
              </a>
            ) : (
              <Link href={href} className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type PublicNavbarProps = {
  forceFullReloadLinks?: boolean;
};

export function PublicNavbar({ forceFullReloadLinks = false }: PublicNavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { url } = usePage();
  const currentPath = url.split('?')[0];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-primary/30 bg-primary">
      <nav
        className="container flex h-16 items-center justify-between"
        aria-label="Navigazione principale"
      >
        {forceFullReloadLinks ? (
          <a href="/" className="flex items-center gap-2" aria-label="Vai alla home">
            <Logo className="h-6 w-36 bg-white" />
          </a>
        ) : (
          <Link href="/" className="flex items-center gap-2" aria-label="Vai alla home">
            <Logo className="h-6 w-36 bg-white" />
          </Link>
        )}

        <div className="hidden md:flex md:items-center md:gap-8">
          {navLinks.map((link) => {
            const isActive = currentPath === link.href;
            const className = `font-medium transition-colors hover:text-white ${
              isActive ? 'text-white' : 'text-white/80'
            }`;
            if (forceFullReloadLinks || link.href.startsWith('/blog')) {
              return (
                <a
                  key={link.href}
                  href={link.href}
                  className={className}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {link.label}
                </a>
              );
            }
            return (
              <Link
                key={link.href}
                href={link.href}
                className={className}
                aria-current={isActive ? 'page' : undefined}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        <div className="hidden md:flex md:items-center md:gap-4">
          <LoginMenu
            forceFullReload={forceFullReloadLinks}
            buttonClassName="bg-white text-primary hover:bg-white/90"
          />
        </div>

        <button
          type="button"
          className="md:hidden p-2 text-white"
          onClick={() => setMobileMenuOpen((open) => !open)}
          aria-label={mobileMenuOpen ? 'Chiudi menu' : 'Apri menu'}
          aria-expanded={mobileMenuOpen}
          aria-controls="public-mobile-menu"
        >
          {mobileMenuOpen ? <X aria-hidden size={24} /> : <Menu aria-hidden size={24} />}
        </button>
      </nav>

      {mobileMenuOpen ? (
        <div id="public-mobile-menu" className="md:hidden border-t border-primary/30 bg-primary">
          <div className="container py-4 flex flex-col gap-4">
            {navLinks.map((link) => {
              const isActive = currentPath === link.href;
              const className = `font-medium py-2 transition-colors hover:text-white ${
                isActive ? 'text-white' : 'text-white/80'
              }`;
              if (forceFullReloadLinks || link.href.startsWith('/blog')) {
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={closeMobileMenu}
                    className={className}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {link.label}
                  </a>
                );
              }
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={closeMobileMenu}
                  className={className}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {link.label}
                </Link>
              );
            })}
            <LoginMenu
              align="start"
              buttonClassName="w-full bg-white text-primary hover:bg-white/90"
              forceFullReload={forceFullReloadLinks}
            />
          </div>
        </div>
      ) : null}
    </header>
  );
}
